/**
 * Calculate the relative luminance of a color
 * Based on WCAG 2.0 formula
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Relative luminance (0-1)
 */
function getLuminance(r, g, b) {
  // Convert to 0-1 range
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const val = c / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  // Calculate relative luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine if white or black text provides better contrast on a given background color
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Either '#ffffff' (white) or '#000000' (black)
 */
export function getContrastColor(r, g, b) {
  const luminance = getLuminance(r, g, b);

  // WCAG contrast ratio formula
  // If luminance is low (dark color), use white text
  // If luminance is high (light color), use black text
  // Threshold of 0.5 works well for most cases
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

/**
 * Parse an rgba color string and return RGB values
 *
 * @param {string} rgbaString - RGBA color string, e.g., "rgba(68, 1, 84, 1)"
 * @returns {{r: number, g: number, b: number}|null} RGB object or null if invalid
 */
export function parseRgba(rgbaString) {
  if (!rgbaString) return null;

  const matches = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!matches) return null;

  return {
    r: parseInt(matches[1]),
    g: parseInt(matches[2]),
    b: parseInt(matches[3]),
  };
}

/**
 * Get the contrasting text color for a given RGBA background color
 *
 * @param {string} rgbaString - RGBA color string, e.g., "rgba(68, 1, 84, 1)"
 * @returns {string} Either '#ffffff' (white) or '#000000' (black)
 */
export function getContrastColorFromRgba(rgbaString) {
  const rgb = parseRgba(rgbaString);
  if (!rgb) return "#000000"; // Default to black if parsing fails

  return getContrastColor(rgb.r, rgb.g, rgb.b);
}

export default getContrastColorFromRgba;
